import { useEffect, useState, useCallback } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Badge,
} from "@illustraclass/ui";
import { Plus, RotateCcw, UserX, UserCheck } from "lucide-react";
import { api, ApiError } from "../../lib/api";

type Teacher = {
  identifier: string;
  email: string;
  name: string;
  status: string;
  dateCreated: string;
};

type TeachersResponse = {
  itemListElement: Teacher[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

export function AdminTeachersPage() {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchTeachers = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const params = new URLSearchParams({ page: String(page), limit: "20" });
        if (search) params.set("search", search);
        const data = await api<TeachersResponse>(
          `/api/admin/teachers?${params.toString()}`,
        );
        setTeachers(data.itemListElement);
        setPagination(data.pagination);
      } catch {
        setFetchError("Impossible de charger la liste des professeurs.");
      } finally {
        setIsLoading(false);
      }
    },
    [search],
  );

  useEffect(() => {
    void fetchTeachers();
  }, [fetchTeachers]);

  async function handleCreate(e: React.SyntheticEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      await api("/api/admin/teachers", {
        method: "POST",
        body: JSON.stringify({ email: newEmail, displayName: newName }),
      });
      setNewEmail("");
      setNewName("");
      setShowCreateForm(false);
      setActionMessage("Professeur créé et invitation envoyée.");
      void fetchTeachers();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Erreur lors de la création.",
      );
    }
  }

  async function handleToggleStatus(id: string, currentStatus: string) {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await api(`/api/admin/teachers/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setActionMessage(
        newStatus === "active"
          ? "Professeur réactivé."
          : "Professeur désactivé.",
      );
      void fetchTeachers();
    } catch (err) {
      setActionMessage(err instanceof ApiError ? err.message : "Erreur.");
    }
  }

  async function handleResetPassword(id: string) {
    try {
      await api(`/api/admin/teachers/${id}/reset-password`, { method: "POST" });
      setActionMessage("Email de réinitialisation envoyé.");
    } catch (err) {
      setActionMessage(err instanceof ApiError ? err.message : "Erreur.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Professeurs</h2>
        <Button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Inviter un professeur
        </Button>
      </div>

      {actionMessage && (
        <div className="rounded-md bg-primary/10 p-3 text-sm text-primary">
          {actionMessage}
          <button
            onClick={() => {
              setActionMessage(null);
            }}
            className="ml-2 underline"
            type="button"
          >
            Fermer
          </button>
        </div>
      )}

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Inviter un professeur</CardTitle>
          </CardHeader>
          <CardContent>
            {formError && (
              <div
                role="alert"
                className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              >
                {formError}
              </div>
            )}
            <form
              onSubmit={(e) => {
                void handleCreate(e);
              }}
              className="flex flex-col gap-4 sm:flex-row sm:items-end"
            >
              <div className="flex-1 space-y-2">
                <Label htmlFor="teacher-email">Email</Label>
                <Input
                  id="teacher-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                  }}
                  required
                  placeholder="professeur@email.fr"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="teacher-name">Nom complet</Label>
                <Input
                  id="teacher-name"
                  type="text"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                  }}
                  required
                  placeholder="Marie Dupont"
                />
              </div>
              <Button type="submit">Envoyer l'invitation</Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-4">
        <Input
          placeholder="Rechercher par nom ou email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          className="max-w-sm"
          aria-label="Rechercher un professeur"
        />
        <span className="text-sm text-muted-foreground">
          {pagination.total} professeur{pagination.total > 1 ? "s" : ""}
        </span>
      </div>

      {fetchError ? (
        <div className="flex flex-col items-center gap-4 py-8">
          <p role="alert" className="text-sm text-destructive">
            {fetchError}
          </p>
          <Button
            variant="outline"
            onClick={() => {
              void fetchTeachers();
            }}
          >
            Réessayer
          </Button>
        </div>
      ) : isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : teachers.length === 0 ? (
        <p className="text-muted-foreground">Aucun professeur trouvé.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Nom
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Email
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Statut
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Créé le
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((teacher) => (
                <tr key={teacher.identifier} className="border-t border-border">
                  <td className="px-4 py-3 font-medium text-foreground">
                    {teacher.name}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {teacher.email}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={
                        teacher.status === "active" ? "default" : "secondary"
                      }
                    >
                      {teacher.status === "active" ? "Actif" : "Inactif"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(teacher.dateCreated).toLocaleDateString("fr-FR")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void handleToggleStatus(
                            teacher.identifier,
                            teacher.status,
                          );
                        }}
                        aria-label={
                          teacher.status === "active"
                            ? "Désactiver"
                            : "Réactiver"
                        }
                      >
                        {teacher.status === "active" ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void handleResetPassword(teacher.identifier);
                        }}
                        aria-label="Réinitialiser le mot de passe"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: pagination.totalPages }, (_, i) => (
            <Button
              key={i}
              variant={pagination.page === i + 1 ? "default" : "outline"}
              size="sm"
              onClick={() => {
                void fetchTeachers(i + 1);
              }}
            >
              {i + 1}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
